/**
 * The BILINGUAL customer receipt.
 *
 * Every other receipt in this package speaks one language: the cashier's app
 * language when the caller supplies one, the tenant's ArabiReset flag when it
 * does not (`receiptArabic`). That is the right default — a slip that says
 * everything twice is twice as long, and on a 72 mm roll length is money.
 *
 * GL_WEB_ablaatiaat is the exception, and asked for it: their counter serves
 * Arabic- and English-speaking customers from the same till, their catalogue is
 * maintained in both scripts, and their kitchen slip has printed both halves of
 * every item since the ERP's per-line options were split (see
 * `utils/legacyLineOptions.ts` in pos-api). The customer receipt was the one
 * document still picking a side.
 *
 * So this is a per-tenant flag, not a print option: nobody should be able to
 * turn a second language on for a tenant whose catalogue only has one, which
 * would print each name twice — and the whole point of the exception is that
 * ablaatiaat's does not have that problem.
 *
 * WHAT IT CHANGES: the 72 mm ticket only, and only its wording. Money, layout,
 * ZATCA QR and every total are untouched — a bilingual receipt is the same
 * receipt with both names on it, and any of those changing would make it a
 * different document.
 */

/**
 * Tenant databases that print the bilingual customer receipt.
 *
 * Matched on the exact `GL_WEB_<slug>` name, case-insensitively, so a test
 * tenant (`GL_WEB_ablaatiaat_test`) is deliberately NOT swept in — it gets the
 * standard slip until it is listed here on purpose.
 */
const BILINGUAL_TENANT_DBS = ['gl_web_ablaatiaat'];

/** Does this tenant print both languages on the customer receipt? */
export function usesBilingualReceipt(pc: { tenantDb?: string | null }): boolean {
  const db = (pc.tenantDb ?? '').trim().toLowerCase();
  return db !== '' && BILINGUAL_TENANT_DBS.includes(db);
}

/**
 * Both scripts on one line, the receipt's own language first.
 *
 * Returns the single value when one half is missing or the two say the same
 * thing — a name that exists once must never print twice, which is exactly the
 * "ملفوف Malfof" duplication this repo has already been bitten by.
 */
export function bothScripts(
  primary: string | null | undefined,
  secondary: string | null | undefined,
): string {
  const p = (primary ?? '').trim();
  const s = (secondary ?? '').trim();
  if (!p) return s;
  if (!s || s === p) return p;
  return `${p} / ${s}`;
}

/**
 * One printed option name, resolved for whichever receipt is being built.
 *
 * `ReceiptData.mods` is a flat `string[]` by the time any renderer sees it, so
 * this choice has to be made where both halves are still in hand — in the
 * cashier's `printAfterCharge` and in `buildReprintData`. Both call this, so
 * the charge-time slip and its reprint can never disagree.
 */
export function receiptModifierName(
  mod: { name?: string | null; nameAr?: string | null; qty?: number | null },
  opts: { arabic: boolean; bilingual: boolean },
): string {
  const en = (mod.name ?? '').trim();
  const ar = (mod.nameAr ?? '').trim();
  const primary = opts.arabic ? ar : en;
  const secondary = opts.arabic ? en : ar;
  const name = opts.bilingual ? bothScripts(primary, secondary) : primary || secondary;
  // A "choices with quantity" option prints its quantity (3.5 m of a fabric);
  // an ordinary option (no qty, or 1) prints exactly as before.
  const q = mod.qty == null ? 1 : Number(mod.qty);
  return name && Number.isFinite(q) && q !== 1 ? `${name} × ${+q.toFixed(3)}` : name;
}
